import { AppBar, Container, Grid, Hidden, makeStyles, Typography } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import React, { FC, ReactElement, ReactNode, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { stepSubTitles, stepTitles } from "../../config/stepTitles";
import { prezzoAnnuoSelector, prezzoFinaleSelector, semestraleSelector } from "../../store/discount/discount.selectors";
import { stepSelector } from "../../store/engine/engine.selectors";
import { Step } from "../../types/Step";
import { BackButton } from "../BackButton";
import { CompleteButton } from "../CompleteButton";
import { Footer } from "../Footer";
import { IntermediarioDetails } from "../IntermediarioDetails";
import { Logo } from "../Logo";
import { GridPanel, SwipeablePanel } from "../Panel/Panel";
import { Stepper } from "../Stepper";
import { SubTitle } from "../SubTitle";
import { Support } from "../Support";
import { Title } from "../Title";

interface Props {
  children: ReactNode;
}

export const LayoutWithPanel: FC<Props> = (props: Props) => {
  const step = useSelector(stepSelector);
  const [mobileMenuVisibile, setMobileMenuVisibile] = React.useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLElement>): void => {
      event && event.preventDefault();
      setMobileMenuVisibile(!mobileMenuVisibile);
    },
    [setMobileMenuVisibile, mobileMenuVisibile],
  );
  const title = stepTitles[step];
  const subTitle = stepSubTitles[step];
  return (
    <>
      {/* Mobile view - start */}
      <Hidden mdUp>
        <Container>
          <Logo onClick={handleToggle} showMenuButton={true} />
          <Grid container>
            <Grid item xs={12}>
              <Stepper />
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item>
                  <Title title={title} />
                  <SubTitle title={subTitle} />
                </Grid>
              </Grid>
              <Grid item>{props.children}</Grid>
            </Grid>
          </Grid>
          <IntermediarioDetails />
          <Support />
          <Footer />
          <SwipeablePanel open={mobileMenuVisibile} onClose={handleToggle} onOpen={handleToggle} />
          <BottomBarMobile />
        </Container>
      </Hidden>
      {/* Mobile view - end */}

      {/* Desktop view - start */}
      <Hidden only={["xs", "sm"]}>
        <Container>
          <Logo onClick={handleToggle} showMenuButton={false} />
          <Grid container>
            <Grid item container sm={8} style={{ height: "100%" }}>
              <Grid item>
                <Title title={title} />
                <SubTitle title={subTitle} />
              </Grid>
              <Grid item>{props.children}</Grid>
            </Grid>

            <GridPanel />
          </Grid>
          <IntermediarioDetails />
          <Support />
          <Footer />
        </Container>
      </Hidden>
      {/* Desktop view - end */}
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  appBar: {
    top: "auto",
    bottom: 0,
    minHeight: 150,
    backgroundColor: theme.palette.secondary.light,
    padding: "16px 20px 31px 20px",
  },
  price: {
    color: theme.palette.primary.main,
  },
  priceDisclaimer: {
    fontSize: 12,
    letterSpacing: 0,
    lineHeight: "16px",
    [theme.breakpoints.down("sm")]: {
      lineHeight: "15px",
      color: theme.palette.primary.contrastText,
      marginLeft: 5,
    },
  },
  rowDettagli: {
    marginBottom: 10,
  },
}));

const BottomBarMobile = (): ReactElement => {
  const classes = useStyles();
  const prezzo = useSelector(prezzoFinaleSelector);
  const prezzoAnnuo = useSelector(prezzoAnnuoSelector);
  const semestrale = useSelector(semestraleSelector);
  const step = useSelector(stepSelector);
  return (
    <AppBar position="fixed" color="primary" className={classes.appBar}>
      <Grid container>
        <Grid item xs={6} className={classes.rowDettagli}>
          <Typography variant={"h6"}>{"Prezzo finale*"}</Typography>
          <Typography variant={"h1"} className={classes.price} data-test="prezzo-finale" component={"span"}>
            {prezzo === undefined ? (
              <CircularProgress size="0.75em" color="inherit" />
            ) : (
              prezzo.toLocaleString("it-IT", { style: "currency", currency: "EUR" })
            )}
          </Typography>
          <Typography variant={"body1"} data-test="prezzo-annuale">
            {step === Step.SCONTO && prezzoAnnuo !== undefined && semestrale && (
              <>(Annuale {prezzoAnnuo.toLocaleString("it-IT", { style: "currency", currency: "EUR" })})</>
            )}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography className={classes.priceDisclaimer}>
            {step === Step.SCONTO
              ? "* Se scelto il pagamento semestrale, conteggiati interessi del 4% con minimo di € 5"
              : "* Prezzo finale che potrà variare a seconda di quello che sceglierai da 80 € a 295 €"}
          </Typography>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={6}>
          <BackButton />
        </Grid>
        <Grid item xs={6}>
          <CompleteButton />
        </Grid>
      </Grid>
    </AppBar>
  );
};
